import * as React from 'react';
import { atomFamily, useRecoilState } from 'recoil';

import type * as api from '@gitbook/api-client';
import { createEditorValue, getEmptyEditorValue } from '@gitbook/doc-core';
import type { Value } from '@gitbook/slate';
import { useStateWithDeps } from '@gitbook/util-react-hooks';

import { persistRecoilEffect, persistRecoilSkip } from '../../utils';
import { decodeDocumentFromAPI, encodeDocumentForAPI } from './encodeDocumentForAPI';

const basicEditorValuesAtom = atomFamily<Value | null, string>({
    default: () => null,
    key: 'basicEditorValuesAtom',
    effects: [
        persistRecoilEffect({
            serializer: {
                serialize: (value) => {
                    if (!value) {
                        return persistRecoilSkip;
                    }

                    return encodeDocumentForAPI(value.document);
                },
                deserialize: (json) => {
                    return createEditorValue(decodeDocumentFromAPI(json), 'basic');
                },
            },
        }),
    ],
});

/**
 * Utility hook to manage the state of a editor value coming from the API.
 */
export function useBasicEditorValue(initialInput?: api.JSONDocument) {
    const initialValue = React.useMemo(() => {
        return createEditorValue(initialInput ? decodeDocumentFromAPI(initialInput) : '', 'basic');
    }, [initialInput]);
    const [value, setValue] = useStateWithDeps(initialValue, [initialValue]);

    return useEditorValue(initialValue, value, setValue);
}

/**
 * Utility to manage the state of an editor value, persisted between reload, used for insertion.
 */
export function useBasicEditorPersistedValue(
    key: string,
    initialValue: Value = getEmptyEditorValue(key, 'basic'),
    options: {
        /**
         * Reset the value when the initial value is changed.
         * @default false
         */
        resetOnInitialValueChange?: boolean;
    } = {}
) {
    const { resetOnInitialValueChange = false } = options;
    const [value, setNullableValue] = useRecoilState(basicEditorValuesAtom(key));

    const setValue: React.Dispatch<React.SetStateAction<Value>> = React.useCallback(
        (input) => {
            if (typeof input === 'function') {
                setNullableValue((prevValue) => input(prevValue || initialValue));
            } else {
                setNullableValue(input);
            }
        },
        [setNullableValue, initialValue]
    );

    React.useEffect(() => {
        if (resetOnInitialValueChange && initialValue) {
            setNullableValue(null);
        }
    }, [resetOnInitialValueChange, initialValue, setNullableValue]);

    return useEditorValue(initialValue, value || initialValue, setValue);
}

function useEditorValue(
    initialValue: Value,
    value: Value,
    setValue: React.Dispatch<React.SetStateAction<Value>>
) {
    const hasChanged = !value.document.equals(initialValue.document);

    const resetValue = React.useCallback(() => {
        setValue(createEditorValue('', 'basic'));
    }, [setValue]);

    const cancelChanges = React.useCallback(() => {
        setValue(initialValue);
    }, [setValue, initialValue]);

    return {
        value,
        initialValue,
        setValue,
        resetValue,
        cancelChanges,
        hasChanged,
    };
}
